import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Avatar from '../ui/Avatar';
import Badge from '../ui/Badge';
import Button from '../ui/Button';

/**
 * Participant List Panel Component
 * Real-time participant tracking with status and actions
 */

const ParticipantListPanel = ({ participants = [], totalCount, onRemoveParticipant, onSendMessage, onExportParticipants }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'responded', 'pending'

  // Filter participants — by name, or by participant number ("7" or "#7")
  const filteredParticipants = participants.filter((participant) => {
    const query = searchQuery.trim().toLowerCase();
    const matchesSearch = participant.name?.toLowerCase().includes(query)
      || (Boolean(participant.participantNumber) && query.replace(/^#/, '') === String(participant.participantNumber));

    if (filterStatus === 'all') return matchesSearch;
    if (filterStatus === 'responded') return matchesSearch && participant.hasResponded;
    if (filterStatus === 'pending') return matchesSearch && !participant.hasResponded;

    return matchesSearch;
  });

  const onlineCount = participants.filter((p) => p.online !== false).length;
  const respondedCount = participants.filter((p) => p.hasResponded).length;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-neutral-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-neutral-900">Participants</h3>
          <div className="flex items-center gap-2">
            <Badge variant="success" size="sm" dot>
              {onlineCount} online
            </Badge>
            <Badge variant="neutral" size="sm">
              {totalCount || participants.length} total
            </Badge>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search participants..."
            className="w-full pl-10 pr-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              filterStatus === 'all'
                ? 'bg-primary-100 text-primary-700'
                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
          >
            All ({participants.length})
          </button>
          <button
            onClick={() => setFilterStatus('responded')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              filterStatus === 'responded'
                ? 'bg-green-100 text-green-700'
                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
          >
            Responded ({respondedCount})
          </button>
          <button
            onClick={() => setFilterStatus('pending')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              filterStatus === 'pending'
                ? 'bg-amber-100 text-amber-700'
                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
          >
            Pending ({participants.length - respondedCount})
          </button>
        </div>
      </div>

      {/* Participant List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {filteredParticipants.length === 0 ? (
          <div className="text-center py-8">
            <svg className="w-12 h-12 mx-auto text-neutral-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p className="text-sm text-neutral-600">
              {searchQuery ? 'No participants match your search' : 'No participants yet'}
            </p>
          </div>
        ) : (
          <AnimatePresence>
            {filteredParticipants.map((participant, index) => (
              <motion.div
                key={participant.id || participant._id || participant.name}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ delay: index * 0.02 }}
                className="group p-3 bg-white rounded-lg border border-neutral-200 hover:border-primary-200 hover:shadow-sm transition-all"
              >
                <div className="flex items-center gap-3">
                  {/* Avatar */}
                  <div className="relative">
                    <Avatar name={participant.name} size="sm" variant="solid" />
                    <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                      participant.online !== false ? 'bg-green-500' : 'bg-neutral-300'
                    }`} />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-neutral-900 truncate">
                        {participant.participantNumber && (
                          <span className="mr-1 font-bold tabular-nums text-primary-700">#{participant.participantNumber}</span>
                        )}
                        {participant.name}
                      </p>
                      {participant.hasResponded && (
                        <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <p className="text-xs text-neutral-500">
                      {participant.email || 'No email'}
                    </p>
                  </div>

                  {/* Actions (shown on hover) */}
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                    {onSendMessage && (
                      <button
                        onClick={() => onSendMessage(participant)}
                        className="p-1.5 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                        title="Send message"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                      </button>
                    )}
                    {onRemoveParticipant && (
                      <button
                        onClick={() => onRemoveParticipant(participant)}
                        className="p-1.5 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Remove participant"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* Additional Info */}
                {participant.organization && (
                  <div className="mt-2 text-xs text-neutral-500 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    {participant.organization}
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>

      {/* Footer Actions */}
      <div className="p-4 border-t border-neutral-200 space-y-2">
        <Button variant="secondary" size="sm" fullWidth onClick={onExportParticipants}>
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export List
        </Button>
        {onSendMessage && (
          <Button variant="ghost" size="sm" fullWidth onClick={() => onSendMessage('all')}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            Notify All
          </Button>
        )}
      </div>
    </div>
  );
};

export default ParticipantListPanel;
